import type { ApiClient, Credentials } from "../api/client";
import { ApiError } from "../api/client";
import type { DeviceStatus } from "../api/types";
import { t } from "../i18n";
import { deviceToken } from "./device-token";
import { keptPairing } from "./kept-pairing";

/** What the API client sends on every request: the device token stored on this device. */
export const tillCredentials: Credentials = {
  deviceToken: () => deviceToken.read(),
};

export type DeviceCheck =
  | { name: "pairing" }
  | { name: "unreachable"; message: string }
  | { name: "blocked" }
  /** Paired as the Tafo app (ober, koerier): that opens at /app, not here (ADR 0011). */
  | { name: "app" }
  /** The licence ended unpaid: only the payment screen (Furkan, 26/10/2026). */
  | { name: "licence"; status: DeviceStatus }
  | { name: "terms"; status: DeviceStatus }
  | { name: "ready"; status: DeviceStatus };

/**
 * Where a device stands: not paired, paused shop, terms to accept, or ready. A ready till is open
 * for everyone (kassa-besluiten, "Personeel & rechten"): a personal code is asked per action only.
 */
export async function checkDevice(api: ApiClient): Promise<DeviceCheck> {
  if (deviceToken.read() === null) {
    // the storage of this browser was emptied, or this is the till put on a home screen: the pairing the
    // browser kept comes back, without a new code
    const kept = await keptPairing(api, "till");
    if (kept.name === "unreachable") return kept;
    if (kept.name === "none") return { name: "pairing" };
    deviceToken.save(kept.deviceToken);
    // a support window of Tafo never takes the till's token: it stays what it was
    if (deviceToken.read() === null) return { name: "pairing" };
  }
  try {
    const status = await api.request<DeviceStatus>("GET", "/device", { retry: true });
    if (status.device.role !== "till") return { name: "app" };
    // a shop in its trial month works like an active one; paused or terminated shops cannot sell
    if (status.shop.status !== "active" && status.shop.status !== "trial") return { name: "blocked" };
    if (status.licence?.blocked === true) return { name: "licence", status };
    return status.terms.accepted ? { name: "ready", status } : { name: "terms", status };
  } catch (error) {
    if (error instanceof ApiError && error.code === "device_not_paired") {
      deviceToken.clear();
      return { name: "pairing" };
    }
    return { name: "unreachable", message: error instanceof ApiError ? error.message : t.errors.unexpected };
  }
}
